import React, { useState, useEffect, useRef } from "react";
import {
  Stage,
  Layer,
  Image as KonvaImage,
  Line,
  Text,
  Group,
  Circle,
} from "react-konva";

const KonvaStage = ({
  dimensions,
  tool,
  polygons,
  setPolygons,
  setIsDrawing,
  imgRef,
  image,
  stageRef,
  isDrawing,
  setRectModalPosition,
  setShowRectModal,
  setSelectedPolygonForModal,
  selectedPolygonForModal,
  isLocked,
  setShowSegments,
  showSegments,
}) => {
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [scaleForDots, setScaleForDots] = useState(1);
  const [currentPoints, setCurrentPoints] = useState([]); // points being drawn
  const imageObjRef = useRef(null);

  // Handle image loading
  useEffect(() => {
    if (!image) return;
    const img = new window.Image();
    img.onload = () => {
      imageObjRef.current = img;
      setIsImageLoaded(true);
    };
    img.src = image.src;
    return () => {
      img.onload = null;
    };
  }, [image]);

  // Drawing logic: collect 4 points, then open modal for text
  const handleStageClick = (e) => {
    if (tool !== "polygon" || isLocked) return;
    if (currentPoints.length >= 4) return; // Prevent adding more than 4 points
    const stage = e.target.getStage();
    const transform = stage.getAbsoluteTransform().copy();
    transform.invert();
    const pointerPosition = transform.point(stage.getPointerPosition());
    // Convert to original image coordinates
    const origX = pointerPosition.x / dimensions.scale;
    const origY = pointerPosition.y / dimensions.scale;
    setCurrentPoints((prev) => {
      if (prev.length >= 4) return prev; // Prevent more than 4 points
      const newPoints = [...prev, { x: origX, y: origY }];
      if (newPoints.length === 4) {
        // Create new polygon region
        const newPoly = {
          points: newPoints,
          id: Date.now().toString(),
        };
        setPolygons((prevPolys) => [...prevPolys, newPoly]);
        setIsDrawing(false);
        setCurrentPoints([]);
        setSelectedPolygonForModal(newPoly);
        const stageRect = stage.container().getBoundingClientRect();
        const modalWidth = 200;
        const modalHeight = 350;
        let modalX = stageRect.left + pointerPosition.x;
        let modalY = stageRect.top + pointerPosition.y;
        if (modalX + modalWidth > window.innerWidth) {
          modalX = window.innerWidth - modalWidth - 10;
        }
        if (modalY + modalHeight > window.innerHeight) {
          modalY = window.innerHeight - modalHeight - 10;
        }
        modalX = Math.max(10, modalX);
        modalY = Math.max(10, modalY);
        setRectModalPosition({
          x: modalX,
          y: modalY,
        });
        setShowRectModal(true);
        return [];
      } else {
        setIsDrawing(true);
        return newPoints;
      }
    });
  };

  // For crosshair
  const handleStageMouseMove = (e) => {
    const stage = e.target.getStage();
    const scale = stage.scaleX();
    setScaleForDots(scale);
    const transform = stage.getAbsoluteTransform().copy();
    transform.invert();
    const pointerPosition = transform.point(stage.getPointerPosition());
    setMousePos({ x: pointerPosition.x, y: pointerPosition.y });
  };

  // Remove polygon or last point with backspace
  useEffect(() => {
    const handleKeyDown = (e) => {
      const tag = e.target.tagName.toLowerCase();
      const isInput = tag === "input" || tag === "textarea";
      if (isInput) return;
      if (e.key === "Backspace") {
        e.preventDefault();
        if (currentPoints.length > 0) {
          setCurrentPoints((prev) => prev.slice(0, -1));
        }
        else if (selectedPolygonForModal) {
          setPolygons((prev) => prev.filter((poly) => poly.id !== selectedPolygonForModal?.id));
          setSelectedPolygonForModal(null);
        } else if (currentPoints.length > 0) {
          setCurrentPoints((prev) => prev.slice(0, -1));
        } else {
          setPolygons((prev) => prev.slice(0, -1));
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [setPolygons, selectedPolygonForModal, setSelectedPolygonForModal, currentPoints]);

  // Click on polygon to open modal
  const handlePolygonClick = (e, poly) => {
    e.cancelBubble = true;
    if (tool !== "move") return;
    const stage = e.target.getStage();
    const pos = stage.getPointerPosition();
    const stageRect = stage.container().getBoundingClientRect();
    const modalWidth = 200;
    const modalHeight = 150;
    let modalX = stageRect.left + pos.x;
    let modalY = stageRect.top + pos.y;
    if (modalX + modalWidth > window.innerWidth) {
      modalX = window.innerWidth - modalWidth - 10;
    }
    if (modalY + modalHeight > window.innerHeight) {
      modalY = window.innerHeight - modalHeight - 10;
    }
    modalX = Math.max(10, modalX);
    modalY = Math.max(10, modalY);
    setSelectedPolygonForModal(poly);
    setRectModalPosition({
      x: modalX,
      y: modalY,
    });
    setShowRectModal(true);
  };

  // Helper: get bounding box for text
  const getPolyBoundingBox = (points) => {
    const xs = points.map((p) => p.x);
    const ys = points.map((p) => p.y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    const maxX = Math.max(...xs);
    const maxY = Math.max(...ys);
    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
    };
  };

  return (
    <div className="relative h-full w-full flex items-center justify-center">
      {image && (
        <img
          ref={imgRef}
          src={image.src}
          alt="object"
          className="object-contain absolute opacity-0 w-full h-full"
          style={{
            pointerEvents: "none",
            maxWidth: "100%",
            maxHeight: "100vh",
            padding: 0,
            margin: 0,
          }}
        />
      )}
      {isImageLoaded ? (
        <Stage
          ref={stageRef}
          width={dimensions.rendered.width}
          height={dimensions.rendered.height}
          onClick={handleStageClick}
          onMouseMove={handleStageMouseMove}
          draggable={tool === "move"}
          style={{
            width: "100%",
            height: "100%",
            maxWidth: "100%",
            maxHeight: "100vh",
            display: "block",
            margin: "auto",
          }}
        >
          <Layer>
            <KonvaImage
              image={imageObjRef.current}
              width={dimensions.rendered.width}
              height={dimensions.rendered.height}
              x={0}
              y={0}
              listening={false}
            />
            {/* Draw current points and polygon */}
            {currentPoints.map((pt, idx) => (
              <Group key={idx}>
                <Circle x={pt.x * dimensions.scale} y={pt.y * dimensions.scale} radius={idx === currentPoints.length - 1 ? 4 / scaleForDots : 3 / scaleForDots} fill={idx === currentPoints.length - 1 ? "#ffb300" : "#00a1ff"} />
                <Text
                  x={pt.x * dimensions.scale + 6}
                  y={pt.y * dimensions.scale - 8}
                  text={`${idx + 1}`}
                  fontSize={12 / scaleForDots}
                  fill="#222"
                  fontStyle="bold"
                  listening={false}
                />
              </Group>
            ))}
            {currentPoints.length > 1 && (
              <Line
                points={currentPoints.flatMap((p) => [p.x * dimensions.scale, p.y * dimensions.scale])}
                stroke="#00a1ff"
                strokeWidth={3 / scaleForDots}
                closed={false}
              />
            )}
            {/* Show preview fill if 3 points placed and mouse is moving for 4th */}
            {currentPoints.length === 3 && (
              <Line
                points={[
                  ...currentPoints.flatMap((p) => [p.x * dimensions.scale, p.y * dimensions.scale]),
                  mousePos.x,
                  mousePos.y,
                ]}
                fill="rgba(0,161,255,0.15)"
                stroke="#00a1ff"
                strokeWidth={2 / scaleForDots}
                closed={true}
                dash={[4, 4]}
                listening={false}
              />
            )}
            {/* Show line from last point to mouse while drawing */}
            {currentPoints.length > 0 && currentPoints.length < 4 && (
              <Line
                points={[
                  ...currentPoints.flatMap((p) => [p.x * dimensions.scale, p.y * dimensions.scale]),
                  mousePos.x,
                  mousePos.y,
                ]}
                stroke="#00a1ff"
                strokeWidth={1 / scaleForDots}
                dash={[4, 4]}
                closed={false}
                listening={false}
              />
            )}
            {/* Draw crosshair */}
            {tool === "polygon" && (
              <>
                <Line
                  key="vertical-line"
                  points={[
                    mousePos.x,
                    0,
                    mousePos.x,
                    dimensions.rendered.height,
                  ]}
                  stroke="#999"
                  strokeWidth={1 / scaleForDots}
                  dash={[4, 4]}
                  listening={false}
                />
                <Line
                  key="horizontal-line"
                  points={[0, mousePos.y, dimensions.rendered.width, mousePos.y]}
                  stroke="#999"
                  strokeWidth={1 / scaleForDots}
                  dash={[4, 4]}
                  listening={false}
                />
              </>
            )}
            {/* Draw saved polygons */}
            {showSegments && polygons.map((poly, idx) => {
              const bbox = getPolyBoundingBox(poly.points.map(p => ({ x: p.x * dimensions.scale, y: p.y * dimensions.scale })));
              return (
                <Group
                  key={poly.id ? poly.id : idx}
                  id={poly.id}
                  onClick={(e) => handlePolygonClick(e, poly)}
                  onTap={(e) => handlePolygonClick(e, poly)}
                  hitStrokeWidth={20}
                >
                  <Line
                    points={poly.points.flatMap((p) => [p.x * dimensions.scale, p.y * dimensions.scale])}
                    fill="rgba(0, 161, 255, 0.3)"
                    closed={true}
                    stroke={
                      isLocked
                        ? "#ff0000"
                        : selectedPolygonForModal?.id === poly.id
                          ? "#030303"
                          : "#03a1fc"
                    }
                    strokeWidth={selectedPolygonForModal?.id === poly.id ? 2 / scaleForDots : 1 / scaleForDots}
                  />
                  {poly.points.map((pt, i) => (
                    <Circle key={i} x={pt.x * dimensions.scale} y={pt.y * dimensions.scale} radius={3 / scaleForDots} fill="#03a1fc" />
                  ))}
                  {poly.class && (
                    <Text
                      x={bbox.x}
                      y={bbox.y}
                      width={bbox.width}
                      height={bbox.height}
                      text={poly.class}
                      fontSize={14 / scaleForDots}
                      fontStyle="bold"
                      fill="#000"
                      align="center"
                      verticalAlign="middle"
                      listening={false}
                    />
                  )}
                </Group>
              );
            })}
          </Layer>
        </Stage>
      ) : (
        <div className="flex items-center justify-center w-full h-full">
          <p>Loading image...</p>
        </div>
      )}
    </div>
  );
};

export default KonvaStage;